import { type ChatContext, type OutgoingChatContext } from './chat-context.models';
import { type ChatAttachment } from './chat-messages.models';
import { toOutgoingChatContext } from './outgoing-chat-context';
import {
  type TemplateConfigurationSetupName,
  type TemplateConfigurationSetupParams,
} from './template-configuration-setup.models';

export type ChatMessageType = 'text' | 'confirmation' | 'form' | 'start_wizard' | 'exit_wizard';

export interface BaseSendChatMessageRequest<T extends ChatMessageType> {
  $type: T;
  previousMessageId?: string;
  context?: OutgoingChatContext;
}

export interface ChatTextMessage extends BaseSendChatMessageRequest<'text'> {
  message: string;
  attachments?: ChatAttachment[];
}

export interface ChatConfirmationMessage extends BaseSendChatMessageRequest<'confirmation'> {
  actionId: string;
  accepted: boolean;
}

interface BaseChatFormAnswer<T extends ChatFormAnswerType> {
  $type: T;
  questionIndex: number;
}

export type ChatFormAnswerType = 'radio' | 'checkbox' | 'time';

export interface ChatSingleChoiceFormAnswer extends BaseChatFormAnswer<'radio'> {
  value: string;
  comment?: string;
}

export interface ChatMultipleChoiceFormAnswer extends BaseChatFormAnswer<'checkbox'> {
  values: string[];
  comment?: string;
}

/** `hh:mm tt`  */
export interface ChatTimeFormAnswer extends BaseChatFormAnswer<'time'> {
  time: string;
}

export type ChatFormAnswer =
  | ChatSingleChoiceFormAnswer
  | ChatMultipleChoiceFormAnswer
  | ChatTimeFormAnswer;

export interface ChatFormMessage extends BaseSendChatMessageRequest<'form'> {
  actionId: string;
  answers: ChatFormAnswer[];
}

/**
 * The backend calls a Template Configuration Setup a "wizard", so `start_wizard`, `exit_wizard`
 * and `wizardName` are its vocabulary, not ours, and must stay spelled its way on the wire.
 * This declaration is the only place the two vocabularies meet - everything the application
 * itself names uses the domain term.
 */
export interface ChatStartTemplateConfigurationSetupMessage
  extends BaseSendChatMessageRequest<'start_wizard'> {
  wizardName: TemplateConfigurationSetupName;
  params: TemplateConfigurationSetupParams;
}

export type ChatExitTemplateConfigurationSetupMessage = BaseSendChatMessageRequest<'exit_wizard'>;

export type SendChatMessageRequest =
  | ChatTextMessage
  | ChatConfirmationMessage
  | ChatFormMessage
  | ChatStartTemplateConfigurationSetupMessage
  | ChatExitTemplateConfigurationSetupMessage;

type WithOptionalContext<T extends { context?: OutgoingChatContext }> = Omit<T, 'context'> & {
  context?: ChatContext;
};

interface WithOptimisticText {
  optimisticText: string;
}

export type SendChatMessageInput =
  | WithOptionalContext<ChatTextMessage>
  | (WithOptionalContext<ChatConfirmationMessage> & WithOptimisticText)
  | (WithOptionalContext<ChatFormMessage> & WithOptimisticText);

export function toOptimisticText(input: SendChatMessageInput): string {
  return input.$type === 'text' ? input.message : input.optimisticText;
}

export function toOutgoingChatMessage(
  input: SendChatMessageInput,
  { context, previousMessageId }: { context: ChatContext; previousMessageId?: string },
): SendChatMessageRequest {
  const outgoingContext = toOutgoingChatContext(context);
  const envelope = { ...(outgoingContext && { context: outgoingContext }), previousMessageId };
  switch (input.$type) {
    case 'text':
      return { $type: 'text', message: input.message, attachments: input.attachments, ...envelope };
    case 'confirmation':
      return {
        $type: 'confirmation',
        actionId: input.actionId,
        accepted: input.accepted,
        ...envelope,
      };
    case 'form':
      return { $type: 'form', actionId: input.actionId, answers: input.answers, ...envelope };
  }
}
